import { BitkitTour, BitkitUIHeader, createBitkitToast, IconSparkle } from '@bitrise/bitkit-v2';
import { useDisclosure } from '@chakra-ui/react/hooks';
import { useCallback, useRef, useState } from 'react';
import { useContext } from 'react';

import useOrganizationUpdate from '@/client/BFF/Organization/useOrganizationUpdate';
import useWorkspacesProjectsUpdateCIConfigExpertAIConfig from '@/client/BFF/Workspaces/Projects/useWorkspacesProjectsUpdateCIConfigExpertAIConfig';
import {
  CIConfigExpertAction,
  CIConfigExpertActionTypes,
  useCiConfigExpert,
} from '@/components/CIConfigExpert/CIConfigExpert';
import CIConfigExpertOptInDialog, {
  CIConfigExpertOptOutReason,
} from '@/components/CIConfigExpert/CIConfigExpertOptInDialog';
import {
  CIConfigExpertOptInDismissMethod,
  trackAssistantOpened,
  trackDocumentationOpened,
  trackOptInAttempted,
  trackOptInPopupDismissed,
  trackOptInPopupShown,
} from '@/components/CIConfigExpert/ciConfigExpertTracking';
import useCIConfigExpertConversation from '@/components/CIConfigExpert/useCIConfigExpertConversation';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { useOverlaySidebar } from '@/components/page/PlatformShell/OverlaySidebar';
import useWorkflowEditorMessageListener from '@/pages/WorkflowEditorPage/hooks/useWorkflowEditorMessageListener';
import postMessageToWorkflowEditor from '@/pages/WorkflowEditorPage/utils/postMessageToWorkflowEditor';
import useWorkflowEditorPageStore from '@/pages/WorkflowEditorPage/WorkflowEditorPage.store';

import useAskAIFeatureHighlight from './useAskAIFeatureHighlight';

type OpenCIConfigExpertPayload = CIConfigExpertAction & { source?: string };

const AskCIConfigExpertButton = () => {
  const conversationId = useWorkflowEditorPageStore((s) => s.conversationId);
  const projectId = useWorkflowEditorPageStore((s) => s.projectId);
  // Not GlobalPropsWithAccount: UIHeader mounts this on account-less pages too, so the slug has
  // to be read defensively -- the hooks below run before the hidden-button return.
  const { account } = useContext(GlobalPropsContext);
  const workspaceSlug = account?.slug ?? '';

  const {
    isAvailable,
    isEnabled,
    optOutReason,
    canOptIn,
    ciConfigSlug,
    history,
    isLoading,
    hasNewReply,
    markAsViewed,
  } = useCIConfigExpertConversation();

  const { open: isOptInDialogOpen, onOpen: openOptInDialog, onClose: closeOptInDialog } = useDisclosure();
  // Kept in state rather than read from the availability, so that opting in -- which makes the reason
  // disappear -- leaves the dialog mounted long enough to animate shut; cleared when the exit
  // animation completes, so a closed dialog doesn't stay mounted.
  const [dialogReason, setDialogReason] = useState<CIConfigExpertOptOutReason | undefined>(undefined);
  // What the editor asked for when its own AI button opened this dialog, so the assistant can pick up
  // where that click left off once the user opts in.
  const pendingAction = useRef<CIConfigExpertAction | undefined>(undefined);
  const [isOptingIn, setIsOptingIn] = useState(false);
  const enableWorkspaceAI = useOrganizationUpdate({ workspaceSlug });
  const enableAssistantForProject = useWorkspacesProjectsUpdateCIConfigExpertAIConfig({
    workspaceSlug,
    projectId: projectId ?? '',
  });
  const { show } = useOverlaySidebar();
  const { hide, visibility: AIDrawerVisibility } = useCiConfigExpert();

  const onApply = useCallback(
    (bitriseYmlContents: string, turnIndex: number, turnCount: number) => {
      postMessageToWorkflowEditor<{
        bitriseYmlContents: string;
        conversationId: string | undefined;
        turnIndex: number;
        turnCount: number;
      }>('CI_CONFIG_RECEIVED', { bitriseYmlContents, conversationId, turnIndex, turnCount });
    },
    [conversationId],
  );

  // The editor flips its own "drawer is open" flag optimistically when its AI buttons post
  // OPEN_CI_CONFIG_EXPERT, and only CI_CONFIG_EXPERT_CLOSED clears it again -- the sidebar emits that
  // as it animates shut, and a sidebar that never opened never animates. So every path that ends
  // without a drawer has to say so, or all six entry points in the editor stay disabled until a
  // reload.
  const answerEditorWithoutDrawer = () => postMessageToWorkflowEditor('CI_CONFIG_EXPERT_CLOSED');

  const optInPopupContext = useRef<{ source?: string; action?: CIConfigExpertActionTypes; canOptIn: boolean }>({
    canOptIn: false,
  });

  const optInPopupTrackingCtx = () => ({
    appSlug: ciConfigSlug,
    ...optInPopupContext.current,
  });

  const startOptIn = (source: string | undefined, action: CIConfigExpertActionTypes | undefined) => {
    // A repeated open request while the popup is already up (the editor can re-post) must not count a
    // second impression or re-attribute the one on screen.
    if (isOptInDialogOpen) return;
    optInPopupContext.current = { source, action, canOptIn };
    trackOptInPopupShown(optInPopupTrackingCtx());
    setDialogReason(optOutReason);
    openOptInDialog();
  };

  const trackDrawerOpened = (source: string | undefined, action?: CIConfigExpertActionTypes) => {
    trackAssistantOpened({
      appSlug: ciConfigSlug,
      source,
      conversationId,
      action: action ?? 'general_ask_ai',
      isResumed: history.length > 0,
      priorTurnCount: history.length,
    });
  };

  const trackEditorInitiatedDrawerOpened = (source: string | undefined, action?: CIConfigExpertActionTypes) => {
    if (!source) return;
    trackDrawerOpened(source, action);
  };

  const openAssistantOrOptIn = () => {
    if (!isEnabled) {
      pendingAction.current = undefined;
      startOptIn('global_header_banner', 'general_ask_ai');
      return;
    }

    if (AIDrawerVisibility === 'closed' || AIDrawerVisibility === 'hidden') {
      markAsViewed();
      postMessageToWorkflowEditor('REQUEST_AI_DRAWER_OPEN');
      trackDrawerOpened('global_header_banner');
    } else {
      answerEditorWithoutDrawer();
      hide();
    }
  };

  const askAIButtonRef = useRef<HTMLButtonElement>(null);
  const { tour } = useAskAIFeatureHighlight({
    enabled: isAvailable && !!ciConfigSlug,
    hasConversationHistory: history.length > 0,
    isLoading,
    targetRef: askAIButtonRef,
  });

  const openAssistant = () => {
    if (!ciConfigSlug) return;
    markAsViewed();
    const action = pendingAction.current;
    pendingAction.current = undefined;

    // Opened from one of the editor's own AI buttons: resume that request. Opened from this button:
    // ask the editor for the current yml the same way a normal open does.
    if (action) {
      show({ variant: 'ci-config-expert', props: { ciConfigSlug, onApply, action: 'general_ask_ai', ...action } });
      trackEditorInitiatedDrawerOpened(optInPopupContext.current.source, action.action);
    } else {
      postMessageToWorkflowEditor('REQUEST_AI_DRAWER_OPEN');
      trackDrawerOpened('global_header_banner');
    }
  };

  const handleOptIn = async () => {
    trackOptInAttempted(optInPopupTrackingCtx());
    setIsOptingIn(true);
    try {
      // The project-level config can only be written once the workspace allows AI at all, so a
      // workspace-level opt-out needs both calls, in this order. Writing the project config is
      // idempotent, which spares us having to know whether this project was configured before the
      // workspace was switched off.
      if (optOutReason === 'disabled-by-workspace') {
        await enableWorkspaceAI.mutateAsync({ aiFeatures: true, source: 'ai_assistant_opt_in_popup' });
        useWorkflowEditorPageStore.setState({ ciConfigExpertAvailability: 'disabled-by-project' });
        setDialogReason('disabled-by-project');
      }
      await enableAssistantForProject.mutateAsync({
        ciConfigExpertSettings: { wfeIntegration: true },
        source: 'ai_assistant_opt_in_popup',
      });
      useWorkflowEditorPageStore.setState({ ciConfigExpertAvailability: 'enabled' });
      closeOptInDialog();
      openAssistant();
    } catch {
      // No answer to the editor here on purpose: the dialog stays open for a retry, so something is
      // still on screen, and its close covers the editor either way.
      createBitkitToast({
        dismissible: true,
        messageText: 'Failed to turn on the AI assistant. Please try again.',
        titleText: 'Error',
        variant: 'critical',
      });
    } finally {
      setIsOptingIn(false);
    }
  };

  // Follows what the dialog is showing rather than the live availability, which the two part ways on
  // once an opt-in succeeds. Absolute, because its whole point is being pasted to whoever can flip it.
  const settingsUrl =
    dialogReason === 'disabled-by-workspace'
      ? `${window.location.origin}/workspaces/${account?.slug}/settings/ai`
      : `${window.location.origin}/app/${ciConfigSlug}/settings/ai`;

  useWorkflowEditorMessageListener('CLOSE_CI_CONFIG_EXPERT', hide);

  // The opt-in check belongs here as well as on the entry points, because the Workflow Editor is
  // served from the CDN by version: an editor build that no longer gates its own AI buttons must not
  // be able to open the assistant for a project that never opted in.
  // `source` rides along only for tracking -- which editor surface's AI button was clicked. It is not
  // part of the action handed back to the assistant.
  useWorkflowEditorMessageListener<OpenCIConfigExpertPayload>(
    'OPEN_CI_CONFIG_EXPERT',
    // The `= {}` guards against an editor build that posts no payload: a throw here would skip
    // answerEditorWithoutDrawer() and wedge the editor's AI buttons until a reload.
    ({ source, ...payload } = {} as OpenCIConfigExpertPayload) => {
      if (!ciConfigSlug) return;
      if (!isAvailable) {
        answerEditorWithoutDrawer();
        return;
      }
      if (!isEnabled) {
        pendingAction.current = payload;
        startOptIn(source, payload.action);
        return;
      }
      markAsViewed();
      show({
        variant: 'ci-config-expert',
        props: {
          ciConfigSlug,
          onApply,
          action: 'general_ask_ai',
          ...payload,
        },
      });
      trackEditorInitiatedDrawerOpened(source, payload.action);
    },
  );

  if (!isAvailable || !ciConfigSlug) {
    return null;
  }

  const handleOptInDialogClose = (method: CIConfigExpertOptInDismissMethod) => {
    trackOptInPopupDismissed({ ...optInPopupTrackingCtx(), method });
    closeOptInDialog();
    answerEditorWithoutDrawer();
  };

  const handleClick = () => {
    openAssistantOrOptIn();
  };

  return (
    <>
      <BitkitUIHeader.Button
        badge={hasNewReply ? 'pulse' : undefined}
        icon={IconSparkle}
        onClick={handleClick}
        ref={askAIButtonRef}
        selected={AIDrawerVisibility === 'visible'}
        variant="secondary"
      >
        Ask AI
      </BitkitUIHeader.Button>
      {dialogReason && (
        <CIConfigExpertOptInDialog
          open={isOptInDialogOpen}
          onDismiss={handleOptInDialogClose}
          onDocsClick={() => trackDocumentationOpened(optInPopupTrackingCtx())}
          onExitComplete={() => setDialogReason(undefined)}
          reason={dialogReason}
          canOptIn={canOptIn}
          isOptingIn={isOptingIn}
          onOptIn={handleOptIn}
          settingsUrl={settingsUrl}
        />
      )}
      <BitkitTour tour={tour} />
    </>
  );
};

export default AskCIConfigExpertButton;
